import axiosInstance from '../api/axios';
import type {
  PaymentSystemInSettings,
  PaymentSystemInSettingsCreateRequest,
  PaymentSystemInSettingsUpdateRequest,
} from '@/types/finance/payment-system-in-settings';

export const financeService = {
  getMethodMap: async () => (await axiosInstance.get('/finance/method-map')).data,
  createMethodGroup: async (data: { name: string; description?: string }) =>
    (await axiosInstance.post('/finance/method-map', data)).data,
  updateMethodGroup: async (id: string, data: { name: string; description?: string }) =>
    (await axiosInstance.put(`/finance/method-map/${id}`, data)).data,
  assignMethodGroup: async (id: string, paymentSystemIds: string[]) =>
    (await axiosInstance.put(`/finance/method-map/${id}/methods`, { payment_system_ids: paymentSystemIds })).data,
  uploadMethodFlow: async (id: string, file: File, paymentSystemId?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (paymentSystemId) form.append('payment_system_id', paymentSystemId);
    return (await axiosInstance.post(`/finance/method-map/${id}/flow`, form)).data;
  },
  openMethodFlow: async (id: string, flowId?: string) => {
    const response = await axiosInstance.get(flowId ? `/finance/method-map/${id}/flows/${flowId}` : `/finance/method-map/${id}/flow`, { responseType: 'blob' });
    window.open(URL.createObjectURL(response.data), '_blank', 'noopener,noreferrer');
  },
  deleteMethodFlow: async (id: string, flowId: string) => axiosInstance.delete(`/finance/method-map/${id}/flows/${flowId}`),
  deleteMethodGroup: async (id: string) => axiosInstance.delete(`/finance/method-map/${id}`),
  createPaymentLink: async (groupId: string, paymentSystemId: string, amount: string) =>
    (await axiosInstance.post<{ payment_form_url: string }>(`/finance/method-map/${groupId}/payment-link`, {
      payment_system_id: paymentSystemId,
      amount,
    })).data,
  getExchangeRates: async () => {
    const response = await axiosInstance.get('/finance/exchange-rate/');
    return response.data;
  },

  createExchangeRate: async (data: { currency_id: string }) => {
    const response = await axiosInstance.post('/finance/exchange-rate', data);
    return response.data;
  },

  deleteExchangeRate: async (id: string | number) => {
    const response = await axiosInstance.delete(`/finance/exchange-rate/${id}`);
    return response.data;
  },

  getTraderExchangeRates: async () => {
    const response = await axiosInstance.get('/finance/trader-exchange-rates/');
    return response.data;
  },

  createTraderExchangeRate: async (data: { currency_id: string; trader_id?: string | null; rate: number | string }) => {
    const response = await axiosInstance.post('/finance/trader-exchange-rates', data);
    return response.data;
  },

  updateTraderExchangeRate: async (id: string, data: { rate: number | string }) => {
    const response = await axiosInstance.put(`/finance/trader-exchange-rates/${id}`, data);
    return response.data;
  },

  deleteTraderExchangeRate: async (id: string) => {
    const response = await axiosInstance.delete(`/finance/trader-exchange-rates/${id}`);
    return response.data;
  },

  getMerchantExchangeRates: async () =>
    (await axiosInstance.get('/finance/merchant-exchange-rates/')).data,

  createMerchantExchangeRate: async (data: { currency_id: string; merchant_id: string; rate: number | string }) =>
    (await axiosInstance.post('/finance/merchant-exchange-rates', data)).data,

  updateMerchantExchangeRate: async (id: string, data: { rate: number | string }) =>
    (await axiosInstance.put(`/finance/merchant-exchange-rates/${id}`, data)).data,

  deleteMerchantExchangeRate: async (id: string) =>
    (await axiosInstance.delete(`/finance/merchant-exchange-rates/${id}`)).data,

  /**
   * Настройки IN для ПС. Если записи нет — дефолты формы
   * (`max_active_in_orders=7`, `min_effective_balance=null`) без `id`.
   */
  getPaymentSystemInSettings: async (
    paymentSystemId: string,
  ): Promise<PaymentSystemInSettings> => {
    const response = await axiosInstance.get<PaymentSystemInSettings>(
      `/finance/payment-systems/${paymentSystemId}/in-settings`,
    );
    return response.data;
  },

  /**
   * Создаёт одну строку in-settings для IN-ПС. Повтор для той же ПС → 409.
   * Дефолты на бэкенде: `max_active_in_orders=7`, `min_effective_balance=null`.
   */
  createPaymentSystemInSettings: async (
    paymentSystemId: string,
    payload: PaymentSystemInSettingsCreateRequest,
  ): Promise<PaymentSystemInSettings> => {
    const response = await axiosInstance.post<PaymentSystemInSettings>(
      `/finance/payment-systems/${paymentSystemId}/in-settings`,
      payload,
    );
    return response.data;
  },

  /**
   * Полная замена полей in-settings по ПС. Если записи нет — 404.
   */
  updatePaymentSystemInSettings: async (
    paymentSystemId: string,
    payload: PaymentSystemInSettingsUpdateRequest,
  ): Promise<PaymentSystemInSettings> => {
    const response = await axiosInstance.put<PaymentSystemInSettings>(
      `/finance/payment-systems/${paymentSystemId}/in-settings`,
      payload,
    );
    return response.data;
  },
};
